import { gql, useQuery, useMutation } from '@apollo/client'
import merge from 'deepmerge'
import {
  MemberDeleteProfileMutation,
  MemberDeleteProfileMutationVariables,
  MemberUpdateProfileMutation,
  MemberUpdateProfileMutationVariables,
  MemberProfileQuery,
  MeQuery
} from 'types/graphql'
import { useNProgress } from 'utils/useNProgress'
import { getMeQuery } from 'modules/user/queries/me'

export const socialAccountsFieldsFragment = gql`
  fragment SocialAccountsFields on UserSocialAccounts {
    instagramUsername
    twitterUsername
    tiktokUsername
    linkedinUrl
  }
`

export const profileFieldsFragment = gql`
  ${socialAccountsFieldsFragment}
  fragment ProfileFields on User {
    id
    bio
    firstName
    lastName
    fullName
    username
    jobTitle
    location
    verificationStatus
    hasCompletedProfile
    hasAddedAdditionalInformation
    avatarFullPath
    level
    yearOfBirth
    gender
    personal {
      pronoun
      birthday
      hasKids
      hasPets
    }
    interests {
      categoryIds
    }
    socialAccounts {
      ...SocialAccountsFields
    }
  }
`

const deleteProfileMutation = gql`
  mutation MemberDeleteProfile {
    removeMe {
      success
    }
  }
`

const updateProfileMutation = gql`
  mutation MemberUpdateProfile($input: UserProfileInput!, $validationScope: UserProfileInputScope) {
    updateProfile(input: $input, validationScope: $validationScope) {
      ...ProfileFields
    }
  }
  ${profileFieldsFragment}
`

const getProfileQuery = gql`
  query MemberProfile {
    me {
      ...ProfileFields
    }
  }
  ${profileFieldsFragment}
`

type UseProfileOptions = {
  onCompleted?: (data: MemberProfileQuery) => void
}
export const useProfile = ({ onCompleted }: UseProfileOptions = {}) => {
  return useQuery<MemberProfileQuery>(getProfileQuery, { onCompleted })
}

type UseUpdateProfileOptions = {
  onCompleted?: (data: MemberUpdateProfileMutation) => void
}

export const useUpdateProfile = ({ onCompleted }: UseUpdateProfileOptions = {}) => {
  const [updateProfile, { loading, error }] = useMutation<MemberUpdateProfileMutation, MemberUpdateProfileMutationVariables>(updateProfileMutation, {
    onCompleted,
    update: (cache, { data }) => {
      const me = cache.readQuery<MeQuery>({ query: getMeQuery })?.me
      const updatedMe = merge<MeQuery['me']>(me, data?.updateProfile)
      cache.writeQuery<MeQuery>({
        query: getMeQuery,
        data: {
          me: updatedMe
        }
      })
    }
  })
  useNProgress(loading)

  return { updateProfile, loading, error }
}

export const useDeleteProfile = () => {
  const [deleteProfile, { loading, error }] = useMutation<MemberDeleteProfileMutation, MemberDeleteProfileMutationVariables>(deleteProfileMutation)
  return { deleteProfile, loading, error }
}
